import { getGeoVariableArray } from "./json-provider"
import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { IndicatorClass } from "./indicator-class"
import checkSubVars from "../utils/checkSubVars"
import { getGeoVariableName } from "../components/VariableDropdown"
import {
  extractRawCoVarOptions,
  coVarKeyMainObservation,
  coVarKeyPrimaryTS,
  CoVarOption,
} from "../utils/extractCoVariables"

export const GEO_VAR_PARAM = "var"
export const VERS_PARAM = "vers"

export interface AppState {
  indicatorClass: IndicatorClass
  geoRegion: string[]
  geoVariable: string | null
  restoredGeoVariable: string | null
  subVariable: string[]
  sourceJson: GeoVarJson | null
  coVarOptions: CoVarOption[]
  xRange: [Date, Date] | null
  yRange: [number, number] | null
  jsonLoadError: string | null
}

const initialState: AppState = {
  indicatorClass: "GEM-GLO",
  geoVariable: null,
  restoredGeoVariable: null,
  geoRegion: [],
  subVariable: [],
  sourceJson: null,
  coVarOptions: [],
  xRange: null,
  yRange: null,
  jsonLoadError: null,
}

function getDefaultGeoRegion(indicatorClass: IndicatorClass) {
  if (indicatorClass === "GEM-EUR") {
    return "EU-27"
  }
  if (indicatorClass === "EWM-EUR") {
    return "Whole-Europe Land 35-70N"
  }
  if (
    indicatorClass === "GEM-AT" ||
    indicatorClass === "EWM-AT"
  ) {
    return "Austria"
  } else {
    return "Global"
  }
}

const getNewSubVariables = (sourceJson: GeoVarJson): string[] => {
  const subVariables: string[] = []
  let subvars = checkSubVars(sourceJson)
  for (let name in subvars) {
    subVariables.push(name)
  }
  return subVariables
}

function extractSubVarsAfterSourceJsonChange(args: {
  sourceJson: GeoVarJson
  state: AppState
}): string[] {
  const newSubVars: string[] = getNewSubVariables(args.sourceJson)
  const oldSubVars: string[] = args.state.subVariable
  const allowedSubVars = newSubVars.filter((subVar) =>
    oldSubVars.includes(subVar)
  )
  if (allowedSubVars.length > 0) {
    return allowedSubVars
  } else {
    return [newSubVars[0]]
  }
}

function addGeoVarNameIntoUrlParams(
  indicatorClass: IndicatorClass,
  geoVar: string
) {
  const geoVarName = getGeoVariableName(indicatorClass, geoVar)
  if (!geoVarName) {
    console.error("addGeoVarNameIntoUrlParams: could not find geoVarName for ", geoVar)
    return
  }
  const encodedGeoVarName = geoVarName.replace(/ /g, "-")
  const currentUrl = new URL(window.location.href)
  currentUrl.searchParams.set(GEO_VAR_PARAM, encodedGeoVarName)
  currentUrl.searchParams.delete(VERS_PARAM)
  // Update the browser's address bar without reloading the page
  window.history.replaceState(null, "", currentUrl.toString())
}

function extractAllowedCoVarsAfterChange(args: {
  intl: any
  state: AppState
  newSubVars: string[] | null
}): CoVarOption[] {
  const coVarOptions = extractRawCoVarOptions({
    selectedRegions: args.state.geoRegion,
    selectedSubVars: args.newSubVars ?? args.state.subVariable,
    sourceJson: args.state.sourceJson,
    intl: args.intl,
  })
  const allowedCoVars = coVarOptions.map((coVar) => coVar.value)
  return args.state.coVarOptions.filter((coVar) =>
    allowedCoVars.includes(coVar.value)
  )
}

const slice = createSlice({
  name: "slice",
  initialState,
  reducers: {
    restorePersistedState: (state, action: PayloadAction<AppState>) => {
      state.indicatorClass = action.payload.indicatorClass
      state.geoVariable = action.payload.geoVariable
      state.restoredGeoVariable = action.payload.geoVariable
      state.geoRegion = action.payload.geoRegion
      state.subVariable = action.payload.subVariable
      state.sourceJson = action.payload.sourceJson
      state.coVarOptions = action.payload.coVarOptions ?? []
    },
    setSubVariable: (
      state,
      action: PayloadAction<{ subVars: [string] | []; intl: any }>
    ) => {
      state.xRange = null
      state.yRange = null
      const subVars = action.payload.subVars
      if (subVars.length === 0) {
        state.subVariable = [state.subVariable[0]]
      } else if (subVars.length > 0) {
        if (subVars.length * state.geoRegion.length <= 6)
          state.subVariable = subVars
      }
      state.coVarOptions = extractAllowedCoVarsAfterChange({
        intl: action.payload.intl,
        state,
        newSubVars: state.subVariable,
      })
    },
    setGeoRegion: (state, action: PayloadAction<string[]>) => {
      state.xRange = null
      state.yRange = null
      // state.coVars = [coVarKeyMainObservation]
      const geoRegion = action.payload
      if (geoRegion.length * state.subVariable.length <= 6) {
        state.geoRegion = geoRegion
      }
    },
    setGeoVariable: (state, action: PayloadAction<string>) => {
      state.yRange = null
      if (state.geoVariable !== action.payload) {
        // avoid reseting coVars during page-refreshes
        state.coVarOptions = []
        state.xRange = null
        state.yRange = null
      }
      state.geoVariable = action.payload
      state.jsonLoadError = null
      const sourceJson = state.sourceJson
      if (!sourceJson) {
        return
      }
      const mainSubvars = checkSubVars(sourceJson)
      if (!mainSubvars || Object.keys(mainSubvars).length === 0) {
        return
      }
      const regions = mainSubvars[Object.keys(mainSubvars)[0]].GeoRegions
      if (state.subVariable.length === 0) {
        state.subVariable = [Object.keys(mainSubvars)[0]]
      }
      if (state.geoRegion.length === 0) {
        state.geoRegion = [Object.keys(regions)[0]]
      }
      addGeoVarNameIntoUrlParams(state.indicatorClass, action.payload)
    },
    setIndicatorClass: (state, action: PayloadAction<IndicatorClass>) => {
      const newIndicatorClass = action.payload
      state.geoVariable = getGeoVariableArray(newIndicatorClass)[0]
      state.indicatorClass = newIndicatorClass
    },
    setSourceJson: (state, action: PayloadAction<GeoVarJson | null>) => {
      const sourceJson = action.payload
      state.sourceJson = sourceJson
      if (!sourceJson) {
        return
      }

      state.subVariable = extractSubVarsAfterSourceJsonChange({
        sourceJson,
        state,
      })
      const defaultKey =
        state.indicatorClass.startsWith("EWM")
          ? coVarKeyPrimaryTS
          : coVarKeyMainObservation
      const defaultCoVarOptions: CoVarOption[] = [{
        value: defaultKey,
        name: defaultKey,
        sdev: false,
        ur: false,
        sdevEnabled: false,
        urEnabled: false,
        hasData: true,
      }]
      if (state.restoredGeoVariable == state.geoVariable) {
        state.restoredGeoVariable = null
        if (state.coVarOptions.length === 0) {
          state.coVarOptions = defaultCoVarOptions
        }
      } else {
        state.geoRegion = [getDefaultGeoRegion(state.indicatorClass)]
        state.coVarOptions = defaultCoVarOptions
      }
    },
    setCoVars: (state, action: PayloadAction<CoVarOption[]>) => {
      state.coVarOptions = action.payload
      state.xRange = null
      state.yRange = null
    },
    setXRange: (state, action: PayloadAction<[Date, Date] | null>) => {
      state.xRange = action.payload
    },
    setYRange: (state, action: PayloadAction<[number, number] | null>) => {
      state.yRange = action.payload
    },
    setJsonLoadError: (state, action: PayloadAction<string | null>) => {
      state.jsonLoadError = action.payload
    },
  },
})
export const reducers = slice.reducer
export const actions = slice.actions

export function getMetaData(appState: AppState): FileMetadata | null {
  const sourceJson = appState.sourceJson
  if (!sourceJson) {
    return null
  }
  return sourceJson.FileMetadata
}
